import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { DotsThreeVertical, ThumbsDown, ChatCircleText, Pepper, X } from '@phosphor-icons/react';

/*
  ChipFeedbackMenu
  ----------------
  Tiny 3-dot popover attached to a meal-plan chip. Rendered via React Portal
  so the popover never gets clipped by the chip's `overflow-hidden` container.

  Actions:
    • 👎 Never suggest      — sets dish_prefs=never immediately (+ rated_low signal)
    • 🌶️ Change ingredient  — free-text tweak (less oil, no onion…) → feedback_note
    • 💬 Leave suggestion   — free-text → feedback_note

  All flows fire /api/meals/signals/log so the self-learning aggregator picks
  them up.
*/

export default function ChipFeedbackMenu({ api, dishName, planId, slot, onDone, testIdPrefix }) {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState(null); // null | 'customize' | 'note'
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const btnRef = useRef(null);
  const menuRef = useRef(null);

  // Position the portal-rendered popover next to the button
  useLayoutEffect(() => {
    if (!open || !btnRef.current) return;
    const rect = btnRef.current.getBoundingClientRect();
    const menuW = 224; // matches w-56
    const gap = 6;
    // Prefer to open to the LEFT of the button so we don't run off screen
    let left = rect.right - menuW;
    if (left < 8) left = 8;
    // Prefer above if too close to the bottom
    const spaceBelow = window.innerHeight - rect.bottom;
    const top = spaceBelow < 260 && rect.top > 260
      ? rect.top - 260 + gap
      : rect.bottom + gap;
    setPos({ top, left });
  }, [open, mode]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      const inBtn = btnRef.current && btnRef.current.contains(e.target);
      const inMenu = menuRef.current && menuRef.current.contains(e.target);
      if (!inBtn && !inMenu) {
        setOpen(false); setMode(null); setText('');
      }
    };
    const onKey = (e) => { if (e.key === 'Escape') { setOpen(false); setMode(null); setText(''); } };
    const onScroll = () => {
      // Re-position on scroll
      if (btnRef.current) {
        const rect = btnRef.current.getBoundingClientRect();
        const menuW = 224;
        let left = rect.right - menuW;
        if (left < 8) left = 8;
        const spaceBelow = window.innerHeight - rect.bottom;
        const top = spaceBelow < 260 && rect.top > 260
          ? rect.top - 260 + 6
          : rect.bottom + 6;
        setPos({ top, left });
      }
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onScroll);
    };
  }, [open]);

  const dishKey = (dishName || '').toLowerCase().trim().replace(/\s+/g, '_');

  const logSignal = async (type, ctx = {}) => {
    try {
      await api.post('/meals/signals/log', {
        type,
        dish_name: dishName,
        dish_key: dishKey,
        slot: slot || null,
        plan_id: planId || null,
        context: ctx,
      });
    } catch (_) { /* fire-and-forget */ }
  };

  const handleNeverSuggest = async () => {
    if (!window.confirm(`Never suggest "${dishName}" again?\n\nThis marks it as 'never' in your food profile so the AI Chef stops proposing it.`)) return;
    setBusy(true);
    try {
      const prefsRes = await api.get('/meals/preferences');
      const dishPrefs = (prefsRes.data || {}).dish_prefs || {};
      dishPrefs[dishName] = 'never';
      await api.put('/meals/preferences', { dish_prefs: dishPrefs });
      await logSignal('rated_low', { reason: 'never_suggest_click', instant: true });
      onDone && onDone();
      setOpen(false);
    } catch (e) {
      window.alert(e.response?.data?.detail || 'Failed to save preference.');
    }
    setBusy(false);
  };

  const submitText = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try {
      const kind = mode === 'customize' ? 'customization' : 'note';
      await logSignal('feedback_note', { kind, text: text.trim() });
      setText('');
      setMode(null);
      setOpen(false);
      onDone && onDone();
    } catch (e) {
      window.alert(e.response?.data?.detail || 'Failed to save feedback.');
    }
    setBusy(false);
  };

  const popover = open ? (
    <div
      ref={menuRef}
      style={{ position: 'fixed', top: pos.top, left: pos.left, width: 224, zIndex: 9999 }}
      className="rounded-xl border border-purple-500/40 bg-neutral-900/95 backdrop-blur p-1.5 shadow-2xl text-left"
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      data-testid={`${testIdPrefix}-feedback-menu`}
    >
      {!mode && (
        <>
          <p className="px-2 py-1 text-[10px] uppercase tracking-wider font-bold text-neutral-500 truncate">
            {dishName}
          </p>
          <button
            onClick={handleNeverSuggest}
            disabled={busy}
            data-testid={`${testIdPrefix}-feedback-never`}
            className="w-full flex items-center gap-2 px-2 py-2 rounded-lg hover:bg-red-500/10 text-[12px] font-semibold text-red-200"
          >
            <ThumbsDown size={13} weight="fill" /> Never suggest this
          </button>
          <button
            onClick={() => setMode('customize')}
            data-testid={`${testIdPrefix}-feedback-customize`}
            className="w-full flex items-center gap-2 px-2 py-2 rounded-lg hover:bg-amber-500/10 text-[12px] font-semibold text-amber-200"
          >
            <Pepper size={13} weight="fill" /> Change ingredient / style
          </button>
          <button
            onClick={() => setMode('note')}
            data-testid={`${testIdPrefix}-feedback-note`}
            className="w-full flex items-center gap-2 px-2 py-2 rounded-lg hover:bg-purple-500/10 text-[12px] font-semibold text-purple-200"
          >
            <ChatCircleText size={13} weight="fill" /> Leave a suggestion
          </button>
        </>
      )}

      {mode && (
        <div className="p-1">
          <div className="flex items-center justify-between mb-1">
            <p className="text-[10px] uppercase tracking-wider font-bold text-neutral-500">
              {mode === 'customize' ? 'Change ingredient / style' : 'Your suggestion'}
            </p>
            <button onClick={() => { setMode(null); setText(''); }} className="text-neutral-500 hover:text-white p-0.5">
              <X size={10} weight="bold" />
            </button>
          </div>
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            autoFocus
            placeholder={mode === 'customize' ? 'e.g., "less oil", "no onion", "add paneer"' : 'e.g., "prefer a lighter version at dinner"'}
            data-testid={`${testIdPrefix}-feedback-text`}
            className="w-full px-2 py-1.5 rounded-lg bg-white/5 border border-white/10 text-[11px] text-white outline-none focus:border-purple-500/40 min-h-[60px] resize-none"
          />
          <button
            onClick={submitText}
            disabled={busy || !text.trim()}
            data-testid={`${testIdPrefix}-feedback-submit`}
            className="w-full mt-1.5 py-1.5 rounded-lg text-[11px] font-bold bg-purple-500/25 text-purple-200 border border-purple-500/40 hover:bg-purple-500/40 disabled:opacity-40"
          >
            Save feedback
          </button>
          <p className="text-[9px] text-neutral-500 mt-1 italic">
            The AI Chef reads all feedback when generating your next menu.
          </p>
        </div>
      )}
    </div>
  ) : null;

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        onClick={(e) => { e.stopPropagation(); setOpen(v => !v); setMode(null); setText(''); }}
        data-testid={`${testIdPrefix}-feedback-btn`}
        title="Feedback / customize"
        className={`inline-flex items-center justify-center w-4 h-4 rounded transition-colors ${open
          ? 'text-purple-300 bg-purple-500/20'
          : 'text-neutral-400 hover:text-purple-300 hover:bg-white/10'}`}
      >
        <DotsThreeVertical size={11} weight="bold" />
      </button>
      {typeof document !== 'undefined' && popover ? createPortal(popover, document.body) : null}
    </>
  );
}
